import { useApolloClient, useMutation } from "@apollo/client";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import Remove from "@mui/icons-material/Remove";
import {
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { extensionFromName } from "components/Document/shared";
import { Dropzone } from "components/Dropzone";
import { DropzoneErrorDisplay } from "components/DropzoneErrorDisplay";
import { gql } from "generated-graphql";
import { DocumentType } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useUploadFiles } from "hooks/useDocumentUpload";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { useUpsertDocuments } from "hooks/useUpsertDocuments";
import pluralize from "pluralize";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { ReactNode, useCallback, useState } from "react";
import { FileRejection } from "react-dropzone";
import { useFacility } from "routes/Customer/Facility/useFacility";

import {
  ATTACH_DOCUMENT_TO_MANIFEST,
  DELETE_DOCUMENT_MANIFEST,
  PROCESSED_MANIFEST_DOCUMENTS_QUERY,
  PROCESSED_MANIFESTS_QUERY,
} from "./api";

// Resolve the parsed manifest (if any) for a just-uploaded document, so a
// discard can detach it before deleting the document — the WasteManifest →
// sourceDocumentId FK is RESTRICT, so the manifest must go first.
const MANIFEST_UPLOAD_SOURCE_WASTE_MANIFEST = gql(`
  query ManifestUploadSourceWasteManifest($id: ID!) {
    document(id: $id) {
      id
      sourceWasteManifest {
        id
      }
    }
  }
`);

type ManifestUploadDialogProps = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  facilityId?: string;
  manifestTrackingNumber?: string;
  generatorId?: string;
  /**
   * When uploading from an existing manifest's drawer, the processed manifest to
   * pre-associate the uploaded document(s) with. Pre-association creates the
   * ProcessedManifestDocument link up front so the document shows in the
   * Documents section immediately, before the async pipeline promotes it to the
   * data source. Omitted from the standalone "Add Manifest" entry point (no
   * manifest exists yet).
   */
  processedManifestId?: string;
  /**
   * Fired when extraction has finished and the user clicks "Review" on the
   * analyzing dialog. The caller is responsible for opening the review dialog
   * with these document IDs — this dialog only handles upload + analyzing handoff.
   */
  onExtractionReady?: (documentIds: string[]) => void;
  /**
   * Fired when the extraction job reaches a terminal state. The drawer uses this
   * to start a bounded poll for the data-source link (written by the
   * process-manifests job, which finishes after extraction).
   */
  onProcessingComplete?: () => void;
};

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
};

export function ManifestUploadDialog({
  open,
  onClose,
  tenantId,
  facilityId,
  manifestTrackingNumber,
  generatorId,
  processedManifestId,
  onExtractionReady,
  onProcessingComplete,
}: ManifestUploadDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const client = useApolloClient();
  const uploadFiles = useUploadFiles();
  const { upsertDocuments } = useUpsertDocuments();
  const { queueManifestDocumentExtraction } = useQueueManifestDocumentExtraction();
  const { createDialog, close } = useMinimizedDialogs();
  const [attachDocumentToManifest] = useMutation(ATTACH_DOCUMENT_TO_MANIFEST);
  const [deleteDocumentManifest] = useMutation(DELETE_DOCUMENT_MANIFEST);
  const [deleteDocument] = useDeleteDocumentMutation();

  const [files, setFiles] = useState<File[]>([]);
  const [dropRejectedFiles, setDropRejectedFiles] = useState<FileRejection[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(true);

  // Discard ("bail out") state. Populated once the upload has been queued, so a
  // user who realizes they grabbed the wrong file can delete it (and any parsed
  // data) straight from the analyzing dialog instead of hunting through the
  // document library.
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<string[]>([]);
  const [analyzingDialogId, setAnalyzingDialogId] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const { data: facilityData } = useFacility(facilityId);
  const facilityName = facilityData?.facility?.name;

  const hasOverrides = !!(facilityId || manifestTrackingNumber || generatorId);
  const hasAnythingToSubmit = files.length > 0;

  // --- Launch analyzing dialog after queuing ---

  const launchAnalyzingDialog = useCallback(
    (jobId: string, documentCount: number) => {
      const dialogId = `manifest-analyzing-${jobId}`;
      setAnalyzingDialogId(dialogId);
      setUploadDialogOpen(false);
      createDialog({
        id: dialogId,
        dialogType: DialogType.DocumentAIAnalyzing,
        props: {
          open: true,
          jobId,
          preferOriginalTitle: true,
          dialogId,
          // Manifest flow opts in to whole-box clickable behavior on the
          // minimized chip (single-click expand instead of hunting for the icon).
          wholeBoxClickable: true,
          onReview: (documentIds: string[]) => {
            // Don't call onClose() here — onDismiss fires when the analyzing
            // dialog closes (which happens right after onReview), so this
            // would close the upload dialog twice.
            if (documentIds.length > 0) {
              onExtractionReady?.(documentIds);
            }
          },
          onComplete: () => {
            // Refresh both the manifest row and the drawer's Documents section
            // so the freshly-uploaded document appears without a manual page
            // refresh (PROCESSED_MANIFEST_DOCUMENTS_QUERY backs that section).
            client.refetchQueries({
              include: [PROCESSED_MANIFESTS_QUERY, PROCESSED_MANIFEST_DOCUMENTS_QUERY],
            });
            // The data-source link is written by the downstream process-manifests
            // job, which runs after extraction — let the drawer poll for it.
            onProcessingComplete?.();
          },
          // Bail-out: open the confirm; the actual delete runs in handleDiscard.
          onDiscard: () => setDiscardConfirmOpen(true),
          discardLabel: `Discard ${pluralize("upload", documentCount)}`,
          // Closing the analyzing dialog without reviewing leaves the manifest(s)
          // saved-but-unreviewed — reassure the user so it isn't a silent dead end.
          onCloseWithoutReview: ({ succeededCount }: { succeededCount: number }) => {
            alerts.info(
              `${pluralize("Manifest", succeededCount)} saved. The document data won't be ` +
                `available until ${succeededCount > 1 ? "they've" : "it's"} been reviewed.`
            );
          },
        },
        onDismiss: onClose,
      });
    },
    [createDialog, client, onClose, onExtractionReady, onProcessingComplete, alerts]
  );

  // --- File management ---

  const handleDrop = useCallback((accepted: File[]) => {
    setFiles((prev) => [...prev, ...accepted]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // --- Submit ---

  const handleSubmit = useCallback(async () => {
    if (!tenantId || !hasAnythingToSubmit) return;

    setIsUploading(true);
    try {
      const storageLinks = await uploadFiles(
        files.map((file) => ({
          file,
          documentData: { tenantId },
        }))
      );

      const validUploads = storageLinks
        .map((storageLink, i) => ({ storageLink, file: files[i] }))
        .filter((entry): entry is { storageLink: string; file: File } => entry.storageLink != null);

      if (validUploads.length < files.length) {
        alerts.error(
          `${files.length - validUploads.length} file(s) failed to upload and were skipped.`
        );
      }

      const documents = validUploads.map(({ storageLink, file }) => ({
        tenantId,
        title: file.name,
        fileExtension: extensionFromName(file.name),
        storageLink,
        documentType: DocumentType.RcraManifest,
        isArchived: false,
      }));

      const created = await upsertDocuments({
        documents,
        currentSubmissionDocument: false,
      });

      const allDocumentIds = (created ?? []).map((doc) => doc.id);
      // Remember what we just created so the analyzing dialog can offer a discard.
      setUploadedDocumentIds(allDocumentIds);

      // Pre-associate with the manifest (when uploading from its drawer) so the
      // document shows in the Documents section immediately — the data-source
      // link is written later by the async pipeline. Best-effort: a failed
      // association shouldn't block extraction, so we log and continue.
      if (processedManifestId && allDocumentIds.length > 0) {
        await Promise.all(
          allDocumentIds.map((documentId) =>
            attachDocumentToManifest({
              variables: { processedManifestId, documentId },
            }).catch(() => undefined)
          )
        );
        await client.refetchQueries({ include: [PROCESSED_MANIFEST_DOCUMENTS_QUERY] });
      }

      if (allDocumentIds.length > 0) {
        const jobs = await queueManifestDocumentExtraction({
          tenantId,
          documentIds: allDocumentIds,
          facilityId,
          manifestTrackingNumber,
          generatorId,
        });

        const jobId = jobs[0]?.job.id;
        if (jobId) {
          launchAnalyzingDialog(jobId, allDocumentIds.length);
        }
      }
    } catch (err) {
      // The file reaches S3 before the document record is written, so a failure
      // after that point leaves the upload stored but unreachable. Nothing here
      // can remove it — say what happened rather than implying the file never
      // left, so a retry that appears to duplicate the upload is expected.
      alerts.error(
        "Couldn't add this manifest. If the file uploaded, it wasn't saved to your documents — try again.",
        err instanceof Error ? err : undefined
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    tenantId,
    hasAnythingToSubmit,
    files,
    facilityId,
    manifestTrackingNumber,
    generatorId,
    processedManifestId,
    attachDocumentToManifest,
    client,
    uploadFiles,
    upsertDocuments,
    queueManifestDocumentExtraction,
    launchAnalyzingDialog,
    alerts,
  ]);

  // --- Discard ("bail out") ---

  const handleDiscard = useCallback(async () => {
    if (uploadedDocumentIds.length === 0) {
      setDiscardConfirmOpen(false);
      return;
    }
    const count = uploadedDocumentIds.length;
    setIsDiscarding(true);
    // Track the source documents we actually delete so that if the loop throws
    // partway, a retry re-runs only the remainder — re-deleting an already-gone
    // document id fails in the authorizer lookup and would wedge the discard.
    const deletedIds = new Set<string>();
    try {
      for (const documentId of uploadedDocumentIds) {
        // Detach any parsed manifest first (RESTRICT FK on sourceDocumentId),
        // then delete the source document and its S3 asset.
        const { data } = await client.query({
          query: MANIFEST_UPLOAD_SOURCE_WASTE_MANIFEST,
          variables: { id: documentId },
          fetchPolicy: "network-only",
        });
        const wasteManifestId = data?.document?.sourceWasteManifest?.id;
        if (wasteManifestId) {
          await deleteDocumentManifest({ variables: { wasteManifestId } });
        }
        await deleteDocument({ variables: { id: documentId, retainAsset: false } });
        deletedIds.add(documentId);
      }
      await client.refetchQueries({
        include: [PROCESSED_MANIFESTS_QUERY, PROCESSED_MANIFEST_DOCUMENTS_QUERY],
      });
      alerts.success(`${pluralize("Upload", count)} discarded.`);
      setDiscardConfirmOpen(false);
      // Closing the analyzing dialog fires its onDismiss (onClose), tearing down
      // the whole add-manifest flow.
      if (analyzingDialogId) {
        close(analyzingDialogId);
      } else {
        onClose();
      }
    } catch (err) {
      // Keep only the documents that still exist so a retry doesn't re-delete
      // the ones we already removed.
      if (deletedIds.size > 0) {
        setUploadedDocumentIds((prev) => prev.filter((id) => !deletedIds.has(id)));
      }
      alerts.error(
        `Failed to discard the ${pluralize("upload", count)}.`,
        err instanceof Error ? err : undefined
      );
    } finally {
      setIsDiscarding(false);
    }
  }, [
    uploadedDocumentIds,
    client,
    deleteDocumentManifest,
    deleteDocument,
    alerts,
    analyzingDialogId,
    close,
    onClose,
  ]);

  // --- Dialog lifecycle ---

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setDropRejectedFiles([]);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={uploadDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        title="Add Manifest"
        hideCancelButton
        primaryActionButtonProps={{
          hideStartIcon: true,
          saveText: hasAnythingToSubmit ? "Add Manifest" : "Close",
          onClick: hasAnythingToSubmit ? handleSubmit : handleClose,
          disabled: isUploading,
          loading: isUploading,
          loadingText: "Uploading...",
        }}
      >
        <Stack spacing={2}>
          {/* 1. Helper text */}
          <Typography color="text.secondary">
            Drag and drop PDF manifest files here, or click to browse. We'll process the manifest
            automatically.
          </Typography>

          {/* 2. Drop zone — taller per Jon's feedback */}
          <Dropzone
            label="Drag and drop your documents or click to select file(s)"
            dragActiveText="Drop files here"
            onDrop={handleDrop}
            accept=".pdf"
            multiple
            disabled={isUploading}
            sx={{ height: theme.spacing(14) }}
            displayUploadErrors={false}
            onDropRejected={setDropRejectedFiles}
          />

          {/* 3. File count label + 4. file list */}
          {files.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {files.length} {pluralize("file", files.length)} selected:
              </Typography>
              <Stack spacing={1}>
                {files.map((file, index) => (
                  <Stack
                    key={`${file.name}-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <ListItem
                      dense
                      sx={{ bgcolor: theme.palette.encampBlue.light, borderRadius: "4px" }}
                    >
                      <ListItemIcon sx={{ minWidth: "unset", paddingRight: theme.spacing(2) }}>
                        <InsertDriveFile />
                      </ListItemIcon>
                      <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
                    </ListItem>
                    <Tooltip title="Remove file">
                      <IconButton
                        size="medium"
                        aria-label="remove-file"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isUploading}
                      >
                        <Remove />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}

          {/* Drop-rejection errors (e.g., wrong extension) */}
          {dropRejectedFiles.length > 0 && (
            <Stack spacing={1}>
              {dropRejectedFiles.map(({ file, errors }, index) => (
                <DropzoneErrorDisplay
                  key={`${file.name}-${index}`}
                  file={file}
                  index={index}
                  errors={errors}
                  setDropRejectedFiles={setDropRejectedFiles}
                  dropRejectedFiles={dropRejectedFiles}
                />
              ))}
            </Stack>
          )}

          {/* 5. Pre-fill context — read-only; closes-and-reopen if mistargeted */}
          {hasOverrides && (
            <Stack>
              {facilityId && <OverrideRow label="Facility" value={facilityName ?? "—"} />}
              {manifestTrackingNumber && (
                <OverrideRow label="Tracking Number" value={manifestTrackingNumber} />
              )}
              {generatorId && <OverrideRow label="Generator ID" value={generatorId} />}
            </Stack>
          )}
        </Stack>
      </Dialog>

      {discardConfirmOpen && (
        <ConfirmDialog
          open
          onClose={() => setDiscardConfirmOpen(false)}
          onConfirm={handleDiscard}
          header={
            uploadedDocumentIds.length > 1 ? "Discard these uploads?" : "Discard this upload?"
          }
          info={`This permanently deletes the uploaded ${pluralize(
            "document",
            uploadedDocumentIds.length
          )} and any manifest data parsed from ${
            uploadedDocumentIds.length > 1 ? "them" : "it"
          }. This can't be undone.`}
          confirmText="Discard"
          loading={isDiscarding}
        />
      )}
    </>
  );
}

function OverrideRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ height: 32 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 130, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {typeof value === "string" ? (
          <Typography variant="body2" noWrap component="div">
            {value}
          </Typography>
        ) : (
          value
        )}
      </Box>
    </Stack>
  );
}
